import { useMemo, useState } from 'react';
import styled from 'styled-components';

import Alert from 'components/shared/Alert';
import Button from 'components/shared/Button';
import Checkbox from 'components/shared/form/Checkbox';
import StandardModal, { Actions, SubHeader } from 'components/shared/StandardModal';
import { simpleDate } from 'lib/date';
import { SyncPatientToEhrAction } from 'models/SyncPatientToEhrAction';
import Workflow from 'models/Workflow';
import { WorkflowValuesInput } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

import { selectedGroupName, VALUE_NAMES } from '../constants';
import RevealableSsn from '../RevealableSsn';

import EditableField from './EditableField';
import { FieldLabel, FieldRow, FieldValueCell } from './FieldRow';
import { MATRIXCARE, optionsForField, syncFieldErrors, syncFields } from './fields';
import { platformLabel } from './SyncPatientBody';

interface Props {
  workflow: Workflow;
  // The step being sent. Read for what the LAST dispatch submitted, so a fix-and-resend does not make
  // the coordinator retype a correction the vendor already rejected the referral over.
  action: SyncPatientToEhrAction;
  // Corrections plus the document selection, both staged on the dispatch. The corrections are NOT
  // written back to the referral: those names are declared inputs of the auto-checks, and writing them
  // back re-ran the denial list and the sex offender check (QA, 2026-08-18).
  onSend: (values: WorkflowValuesInput, outputs: WorkflowValuesInput) => Promise<unknown>;
  onClose: () => void;
  // Reopened to fix a field the vendor rejected — skip the document step and focus the MRN.
  startOnReview?: boolean;
}

// One rhythm for the whole modal: 24px between a step's blocks, and 16px on top of the 24 that
// StyledContent already contributes, so the first block sits 40px clear of the header. Both numbers
// are ReferralModal's, measured off the mock there rather than re-eyeballed here.
//
// The gap belongs to the step container, not to each block: as a plain `block` this stacked the
// buttons flush against whatever copy preceded them, and every new note or warning had to remember
// to bring its own margin. Owning it here means a step added later is spaced correctly by default.
const Form = styled.div<{ $hidden?: boolean }>`
  display: ${({ $hidden }) => ($hidden ? 'none' : 'flex')};
  flex-direction: column;
  gap: 24px;
  padding-top: 16px;
`;

// Lists keep their own tighter rhythm: a gap between the bordered rows themselves (Figma
// 11650:17554) rather than each row's own padding, so the boxes read as a stack of cards, not a
// bare list.
const DocList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
`;

// The read-only "Selected Facility" row (AC 15, Figma 11573:11434) — FieldRow/FieldLabel/
// FieldValueCell (shared with EditableField's own reviewable rows) minus the pencil: this value is
// the referral's outcome, not something to correct from inside the sync modal. No `$divider` —
// it's the last row in the list, so no line under it.
const FacilityRow = FieldRow;
const FacilityLabel = FieldLabel;
const FacilityValue = FieldValueCell;

// Regular weight (design review), matching EditableField's own values on this step.
const FacilityValueText = styled(BodySmall)`
  font-weight: 400;
`;

// A selected file's row and filename both pick up the blue treatment (Figma) — a selected row is
// the one that reads as "this is going", so it gets the same accent the rest of the app uses for
// an active choice, not just its checkbox.
//
// A div with its own onClick, not a <label>: the shared Checkbox is a styled div, not a native
// input, so a wrapping <label> would no longer auto-toggle it on a click anywhere in the row. The
// row's onClick is the single source of truth — Checkbox's own onChange is a no-op below so a
// direct click on it doesn't double-toggle through both handlers.
const DocRow = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid ${({ $selected }) => ($selected ? colors.blue100 : colors.black10)};
  border-radius: 4px;
  background: ${({ $selected }) => ($selected ? colors.blue5 : 'transparent')};
  cursor: pointer;
`;

const DocName = styled(BodySmall)<{ $selected: boolean }>`
  color: ${({ $selected }) => ($selected ? colors.blue100 : colors.black100)};
`;

const DocMeta = styled(BodySmall)`
  color: ${colors.black50};
`;

const FilesHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SelectAll = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${colors.blue100};
  text-decoration: underline;
`;

const Note = styled(BodySmall)`
  color: ${colors.black50};
`;

const ErrorSummary = styled(BodySmall)`
  color: ${colors.red100};
`;

// The mock opens the step with an instruction, not a label: it says what to do with the list below
// rather than restating the destination the modal header already names. The form's gap owns the space
// underneath, so SubHeader's own 16px is dropped or it stacks into a hole above the first field.
const Lead = styled(SubHeader)`
  margin-bottom: 0;
  color: ${colors.black100};
`;

/**
 * Review-and-send, before a patient is written into a real EHR (OLIO-2846, delivery in OLIO-2733).
 *
 * Steps, in the screenshots' order: pick the documents, review the demographics, then confirm the
 * permanent EHR write. There is no stepper component in the repo, so this follows ReferralModal's house
 * pattern — the inactive step is HIDDEN rather than unmounted, so Back restores the selection and any
 * in-progress field edit instead of resetting them.
 *
 * The document step is MatrixCare-only: Resident.Document.Add delivers the selected PDFs after the
 * patient import (OLIO-2733). PCC has no document step yet, so a PCC sync skips it entirely — offering a
 * picker that delivers nothing would imply otherwise.
 */
export default function SyncPatientModal({ workflow, action, onSend, onClose, startOnReview = false }: Props) {
  const platform = workflow.ehrSync.platform;
  const platformName = platformLabel(platform);
  const fields = useMemo(() => syncFields(platform), [platform]);

  // The document chooser exists only for MatrixCare — Resident.Document.Add is the one path that
  // actually delivers files (OLIO-2733). PCC has no document step yet, so offering a picker there would
  // imply a delivery that does not happen; a PCC sync skips straight to review.
  const skipDocuments = platform !== MATRIXCARE;

  // Three steps for MatrixCare: pick documents, review demographics, then an explicit confirmation that
  // the sync writes a PERMANENT EHR record (OLIO-2733). PCC (and a `startOnReview` rejected-field fix)
  // skip the document step and land on review.
  const [step, setStep] = useState<'documents' | 'review' | 'confirm'>(
    startOnReview || skipDocuments ? 'review' : 'documents'
  );
  const [sending, setSending] = useState(false);

  // What the REFERRAL holds. The diff below is always taken against this, never against what is on
  // screen — that distinction is the whole reason a resend works: a correction inherited from the last
  // attempt still differs from the referral, so it is sent again rather than computing an empty diff
  // and silently reverting to the referral's value.
  const committed = useMemo(
    () => Object.fromEntries(fields.map((field) => [field.name, workflow.valueFor(field.name)?.value ?? ''])),
    [fields, workflow]
  );

  // What the screen opens with: the referral, overlaid with whatever the last dispatch submitted. So a
  // vendor rejection reopens showing the corrected data the coordinator already typed.
  const opening = useMemo(() => ({ ...committed, ...action.submittedDemographics }), [committed, action]);
  const [values, setValues] = useState<Record<string, string>>(opening);

  // One row open at a time, per the mock. Owned here rather than per row because a select or a date
  // only leaves edit mode by being changed: a row opened and left alone stayed open, and opening a
  // second one put two inputs on screen at once.
  const [editingField, setEditingField] = useState<string | null>(null);

  // The packet is already on the workflow — no fetch. Default to everything selected, since the
  // coordinator opened this to send the referral's documents — EXCEPT on a fix-and-resend, where we
  // read back what the last dispatch actually sent (mirroring `opening` for the demographics). Without
  // this, a coordinator who deselected a file, hit a fixable rejection, and reopened on the review step
  // would silently re-include every file, since the document step that would let them change it is
  // skipped. Filtered to the referral's current attachments so a since-removed id is not resent.
  const [selectedDocs, setSelectedDocs] = useState<string[]>(() => {
    const all = workflow.attachments.map((a) => a.id);
    const prior = action.submittedAttachmentIds;
    return prior ? all.filter((id) => prior.includes(id)) : all;
  });

  const errors = syncFieldErrors(values, platform);
  const blocked = Object.keys(errors).length > 0;

  const hasAttachments = workflow.attachments.length > 0;
  // Files exist but the coordinator cleared them all — a warning, not a block: they may intend to
  // upload manually. Distinct from "no files attached to the referral at all".
  const noneSelected = hasAttachments && selectedDocs.length === 0;

  const setValue = (name: string, value: string) => setValues((prev) => ({ ...prev, [name]: value }));

  const toggleDoc = (id: string) =>
    setSelectedDocs((prev) => (prev.includes(id) ? prev.filter((docId) => docId !== id) : [...prev, id]));

  const send = async () => {
    setSending(true);
    try {
      // Everything that differs from the REFERRAL, which includes a correction carried over from the
      // last attempt. Diffing against what the screen opened with instead would send nothing on an
      // unedited resend, and the vendor would receive the value the referral holds — the one it just
      // rejected.
      const changed = Object.fromEntries(Object.entries(values).filter(([name, value]) => value !== committed[name]));
      // PCC has no document step, so nothing was chosen — send an empty selection rather than the
      // all-selected default, which would record files the PCC path never delivers.
      await onSend(changed, { selected_attachment_ids: skipDocuments ? [] : selectedDocs });
      onClose();
    } catch {
      // Stay OPEN and swallow. The toast already carries the server's own sentence, and closing on a
      // failure is the specific bug this catch exists to prevent: the coordinator would be left
      // looking at a card that had not changed, with no way to tell whether the send had happened.
      // Swallowing rather than rethrowing keeps a failed send from surfacing as an unhandled
      // rejection — this handler IS how the failure is dealt with.
    } finally {
      // Reached on both paths; harmless after a close, and on failure it is what stops the button
      // spinning forever over an error the coordinator needs to act on.
      setSending(false);
    }
  };

  return (
    // 560px explicitly: StandardModal defaults to flex-basis 480px with `flex: 0`, so it never grows
    // into its 560 max — and the label/value rows wrap at 480.
    //
    // One constant title across every step (Figma 11573-8839, AC #17) rather than a per-step
    // sentence — the step's own Lead/heading line says what's being asked; the modal chrome just
    // names what the whole flow is for.
    <StandardModal title={`Push Patient to ${platformName}`} onCancel={onClose} maxWidth='560px'>
      {!skipDocuments && (
        <Form $hidden={step !== 'documents'} data-testid='sync-step-documents'>
          {!hasAttachments ? (
            // AC: no files attached — the modal still opens and can proceed, but notes that files must
            // be uploaded to the EHR by hand.
            <Alert data-testid='sync-no-documents'>
              <Alert.Text>
                This referral has no documents to send. You&apos;ll need to upload any files to {platformName} manually.
              </Alert.Text>
            </Alert>
          ) : (
            <>
              <Lead>
                Choose which files to push with this record. All files attached to this referral are shown below.
              </Lead>
              <FilesHeaderRow>
                <BodySmallBold>
                  Selected Files ({selectedDocs.length}/{workflow.attachments.length})
                </BodySmallBold>
                <SelectAll
                  type='button'
                  onClick={() =>
                    setSelectedDocs(
                      selectedDocs.length === workflow.attachments.length ? [] : workflow.attachments.map((a) => a.id)
                    )
                  }
                  data-testid='sync-select-all'>
                  {selectedDocs.length === workflow.attachments.length ? 'Deselect All' : 'Select All'}
                </SelectAll>
              </FilesHeaderRow>
              <DocList>
                {workflow.attachments.map((attachment) => {
                  const selected = selectedDocs.includes(attachment.id);
                  return (
                    <DocRow key={attachment.id} $selected={selected} onClick={() => toggleDoc(attachment.id)}>
                      <Checkbox
                        checked={selected}
                        onChange={() => {}}
                        size={14}
                        checkedColor={colors.blue100}
                        dataTestId={`sync-doc-${attachment.id}`}
                      />
                      <DocName $selected={selected}>{attachment.filename ?? 'Document'}</DocName>
                      {/* No file size: the workflow payload carries only id/filename/docType. Size is in
                      the manifest the platform records, which is where it is actually reported. */}
                      <DocMeta>{attachment.docType ?? ''}</DocMeta>
                    </DocRow>
                  );
                })}
              </DocList>
              {/* AC: deselecting every file is allowed, but noted — the coordinator may mean to upload
                by hand, so this informs rather than blocks. */}
              {noneSelected && (
                <Alert data-testid='sync-none-selected'>
                  <Alert.Text>
                    No files will be pushed. You&apos;ll need to upload documents to {platformName} manually.
                  </Alert.Text>
                </Alert>
              )}
            </>
          )}
          {platform === MATRIXCARE && hasAttachments && (
            <Note data-testid='sync-pdf-only'>
              Only PDF files can be sent to {platformName}; other file types are skipped and must be uploaded manually.
            </Note>
          )}
          <Actions>
            <Button variant='ghost' size='small' onClick={onClose}>
              Cancel
            </Button>
            <Button variant='primary' size='small' onClick={() => setStep('review')} data-testid='sync-next'>
              Next
            </Button>
          </Actions>
        </Form>
      )}

      <Form $hidden={step !== 'review'} data-testid='sync-step-review'>
        <Lead>Review the fields below before sending to {platformName}.</Lead>

        <Fields>
          {fields.map((field) => (
            <EditableField
              key={field.name}
              field={field}
              value={values[field.name] ?? ''}
              onChange={(value) => setValue(field.name, value)}
              options={optionsForField(field.name)}
              error={errors[field.name]}
              isEditing={editingField === field.name}
              onStartEdit={() => setEditingField(field.name)}
              onEndEdit={() => setEditingField((current) => (current === field.name ? null : current))}
              // Masked while reading, raw while editing — and NOT masked when it is invalid, because
              // masking a value you are asking someone to fix is the actual bug (the mask is what made a
              // redacted number invisible in the first place).
              // The date reads MM/DD/YYYY here while the stored value stays ISO — display only, so the
              // diff that decides what gets sent is untouched. Falls back to the raw string when it
              // won't parse: an unreadable capture is still what we hold, and blanking it would hide
              // the very thing the coordinator opened this screen to fix.
              renderValue={
                field.name === VALUE_NAMES.PATIENT_SSN && !errors[field.name]
                  ? (value) => <RevealableSsn ssn={value} />
                  : field.type === 'date'
                    ? (value) => <>{simpleDate(value) ?? value}</>
                    : undefined
              }
            />
          ))}
          {selectedGroupName(workflow) && (
            <FacilityRow data-testid='sync-selected-facility'>
              <FacilityLabel>Selected Facility</FacilityLabel>
              <FacilityValue>
                <FacilityValueText>{selectedGroupName(workflow)}</FacilityValueText>
              </FacilityValue>
            </FacilityRow>
          )}
        </Fields>

        {blocked && (
          <ErrorSummary data-testid='sync-blocked'>
            Correct the highlighted fields before sending to {platformName}.
          </ErrorSummary>
        )}

        <Actions>
          <Button
            variant='ghost'
            size='small'
            onClick={() => (startOnReview || skipDocuments ? onClose() : setStep('documents'))}
            data-testid='sync-back'>
            {startOnReview || skipDocuments ? 'Cancel' : 'Back'}
          </Button>
          <Button
            variant='primary'
            size='small'
            disabled={blocked}
            onClick={() => setStep('confirm')}
            data-testid='sync-review-next'>
            Next
          </Button>
        </Actions>
      </Form>

      <Form $hidden={step !== 'confirm'} data-testid='sync-step-confirm'>
        {/* AC: an explicit confirmation that the push is a PERMANENT EHR record. Load-bearing — this is
            the last stop before an irreversible write into a real chart system. */}
        <Alert data-testid='sync-permanent-warning'>
          <Alert.Text>
            Pushing creates a <strong>permanent record</strong> in {platformName} that cannot be undone.
          </Alert.Text>
        </Alert>
        {/* Documents are part of the sync only where they are actually delivered (MatrixCare). PCC has
            no document step, so the confirmation says nothing about files it will not send. */}
        {!skipDocuments && (
          <Note data-testid='sync-confirm-summary'>
            {noneSelected || !hasAttachments
              ? `No documents will be sent — you'll need to upload them to ${platformName} manually.`
              : `${selectedDocs.length} document${selectedDocs.length === 1 ? '' : 's'} will be sent to ${platformName}.`}
          </Note>
        )}
        <Actions>
          <Button variant='ghost' size='small' onClick={() => setStep('review')} data-testid='sync-confirm-back'>
            Back
          </Button>
          <Button
            variant='primary'
            size='small'
            disabled={blocked || sending}
            onClick={send}
            data-testid='sync-confirm-send'>
            {sending ? 'Sending…' : `Confirm and Push to ${platformName}`}
          </Button>
        </Actions>
      </Form>
    </StandardModal>
  );
}
